/**
 * Words for the inspection report
 * (claude-docs/2026-10-15-inspection-report-design.md): a vocabulary code in
 * plain words, a room's heading, and a counted list of surfaces
 * ("Floor, ceiling, 2 walls, cabinet"). Pure.
 */

const { roomDisplayName } = require('../rooms/roomDisplayName');

// Codes whose own words read badly. Every other code is spelled out from its
// hyphens (`water-stain` → "Water stain") or its camel case.
const LABELS = {
  // a wall's second finish's place (`material.second.place`)
  accent: 'Accent panel',
  lvp: 'Luxury vinyl plank',
  'painted-flat': 'Flat paint',
  'drop-tile': 'Drop ceiling tile',
  'ceramic-porcelain-tile': 'Ceramic or porcelain tile',
  'brick-stone': 'Brick or stone',
  'shiplap-beadboard': 'Shiplap or beadboard',
  'mirror-glass': 'Mirror or glass',
  'cast-iron-enamel': 'Enamelled cast iron',
  'fiberglass-acrylic': 'Fiberglass or acrylic',
  'laminate-thermofoil': 'Laminate or thermofoil',
  // a device's finish (`deviceFinishes`): an almond plate, as a reader says it
  'ivory-almond': 'Ivory or almond',
  // a room's parts, as its finishes are dated (`depth_conditions_room_finishes`)
  'tub-shower': 'Tub and shower',
  'doors-trim': 'Doors and trim',
  'mold-mildew': 'Mold or mildew',
  'scratched-or-gouged-flooring': 'Scratched or gouged floor',
  'warped-or-lifted-flooring': 'Warped or lifted floor',
  'scorch-or-burn': 'Scorch or burn mark',
  storage: 'Cabinet',
  washerDryer: 'Washer and dryer',
  'wall-face': 'Wall',
  'lower-cabinet': 'Base cabinet',
  'wall-cabinet': 'Upper cabinet',
  'vanity-cabinet': 'Vanity',
  'sink-cabinet': 'Sink base',
  'leg-cabinet': 'Cabinet on legs',
  // devices
  'gfci-outlet': 'GFCI outlet',
  'outlet-240v': 'Outlet, 240 volt',
  '3way': 'Three way switch',
  'co-detector': 'CO detector',
  'hvac-vent': 'HVAC vent',
};

// Plurals the trailing-s rule gets wrong.
const PLURALS = { 'Washer and dryer': 'washers and dryers' };

function words(code) {
  const s = String(code)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/-/g, ' ')
    .toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A vocabulary code in words: `water-stain` → "Water stain", `storage` → "Cabinet". */
function codeLabel(code) {
  return LABELS[code] ?? words(code);
}

/** Does a material class say anything a reader can use? */
const isNamed = (cls) => Boolean(cls) && cls !== 'other' && cls !== 'unknown';

/** A material's own class in words, or null when the model could not name it. */
const firstMaterial = (material) =>
  material && isNamed(material.class) ? codeLabel(material.class) : null;

/** A second finish's place in lower-case words, or null for `other`. */
const placeWords = (second) =>
  second.place && second.place !== 'other'
    ? codeLabel(second.place).toLowerCase()
    : null;

/**
 * A wall's second finish (`material.second`) in lower-case words, by where it is:
 * "tile backsplash", "wallpaper accent panel" — its place alone when its material
 * is not one a wall has ("backsplash": a marble mosaic the model named outside
 * the wall's list), null when it names neither.
 */
function secondFinishWords(second) {
  if (!second) return null;
  const what = isNamed(second.class)
    ? codeLabel(second.class).toLowerCase()
    : null;
  return [what, placeWords(second)].filter(Boolean).join(' ') || null;
}

const capitalized = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * A surface's material in words, a wall's second finish after it ("Painted
 * drywall, tile backsplash"), or null when the model could name neither.
 */
function materialLabel(material) {
  const first = firstMaterial(material);
  const second = material ? secondFinishWords(material.second) : null;
  if (first && second) return `${first}, ${second}`;
  if (second) return capitalized(second);
  return first;
}

/** A wall's second finish by its colour and where it is: "cream backsplash"; '' with no colour. */
function secondFinishColour(second) {
  const colour = second && second.color && second.color.name;
  if (!colour || !secondFinishWords(second)) return '';
  const where = placeWords(second) || codeLabel(second.class).toLowerCase();
  return `${colour.toLowerCase().trim()} ${where}`;
}

/**
 * A room's heading: its name the way a person writes it ("livingRoom" →
 * "Living room", `rooms/roomDisplayName`), or "Room N" when it has none.
 */
function roomTitle(roomName, roomIndex) {
  return roomDisplayName(roomName) ?? `Room ${Number(roomIndex) + 1}`;
}

/**
 * What a surface is, before any letter: "Wall", "Floor", "Ceiling", or the
 * object's label ("Object" for one the scan did not type).
 */
function surfaceKindLabel(doc) {
  if (doc.kind !== 'object') return codeLabel(doc.kind);
  const type = doc.anchor && doc.anchor.type;
  return type ? codeLabel(type) : 'Object';
}

function plural(label) {
  if (PLURALS[label]) return PLURALS[label];
  const lower = label.toLowerCase();
  return /(s|sh|ch|x)$/.test(lower) ? `${lower}es` : `${lower}s`;
}

/**
 * Labels counted, in the order first seen: ['Floor', 'Wall', 'Wall', 'Cabinet']
 * → "Floor, 2 walls, cabinet". An empty list → ''.
 */
function countedList(labels) {
  const counts = new Map();
  for (const l of labels) counts.set(l, (counts.get(l) || 0) + 1);
  const text = [...counts]
    .map(([l, n]) => (n === 1 ? l.toLowerCase() : `${n} ${plural(l)}`))
    .join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A length for a homeowner, to the nearest inch: 0.5 → `1' 8"`, 0.2 → `8"`,
 * 0.914 → `3'`. (`dimensioning/feetInches` gives sixteenths, for a drawing.)
 */
function feetAndInches(meters) {
  const inches = Math.round(meters / 0.0254);
  const feet = Math.floor(inches / 12);
  const rest = inches - feet * 12;
  if (!feet) return `${rest}"`;
  return rest ? `${feet}' ${rest}"` : `${feet}'`;
}

/** Look codes that say nothing a reader can use. */
const NO_LOOK = new Set(['unknown', 'other']);

/**
 * What a surface or fixture looks like, in a few words — its colour and its
 * named look: "Light gray, knockdown or orange peel", "White, flat slab". The
 * homeowner reads it beside the material; never the era or whether the look
 * reads as dated, which stay off their copy. Words the material already says
 * are not said again ("stainless" on a stainless fridge). A wall's second
 * finish follows by its colour: "Greige; cream backsplash". '' with nothing to
 * say.
 */
function finishLabel(doc) {
  const material = ((doc && firstMaterial(doc.material)) || '').toLowerCase();
  const color =
    (doc && doc.material && doc.material.color && doc.material.color.name) ||
    '';
  const code = doc && doc.style && doc.style.look;
  const look = code && !NO_LOOK.has(code) ? code.replace(/-/g, ' ') : '';
  const words = [];
  // a phrase holding the material's own name says nothing new ("stainless
  // steel" on stainless); a multi-word material is matched whole
  const saysMaterial = (w) =>
    Boolean(material) && (w === material || ` ${w} `.includes(` ${material} `));
  for (const w of [color, look].map((x) => x.toLowerCase().trim())) {
    if (!w || saysMaterial(w) || words.includes(w)) continue;
    words.push(w);
  }
  const second = secondFinishColour(doc && doc.material && doc.material.second);
  const text = [words.join(', '), second].filter(Boolean).join('; ');
  return text ? capitalized(text) : '';
}

/**
 * A device's finish in words (`deviceFinishes`: an outlet's plate colour, a
 * faucet's or a light's metal), or null when the model could not tell or named
 * none on the list.
 */
function deviceFinishLabel(finish) {
  return isNamed(finish) ? codeLabel(finish) : null;
}

module.exports = {
  finishLabel,
  deviceFinishLabel,
  feetAndInches,
  codeLabel,
  materialLabel,
  roomTitle,
  surfaceKindLabel,
  countedList,
};
